import { SUPPORT_URL } from '@paper-monster/web/src/env'

import CheckoutSideEmailNeatDocs from '@/components/blocks/CheckoutSideEmailNeatDocs'
import CheckoutSideCreditCardTrialNeatDocs from '@/components/blocks/CheckoutSideCreditCardTrialNeatDocs'
import CheckoutSideCreditCardNeatDocs from '@/components/blocks/CheckoutSideCreditCardNeatDocs'

export const ENV = process.env.NEXT_PUBLIC_ENV

export const IS_PROD = ENV === 'production'

const getAppUrl = () => {
  switch (ENV) {
    case 'qa':
      return 'https://www-qa.tryneat.com'
    case 'staging':
      return 'https://www-staging.tryneat.com'
    case 'development':
      return 'http://localhost:3001'
    default:
      return 'https://www.tryneat.com'
  }
}

export const APP_URL = getAppUrl()

// neat.com's IDs: each one's allowlist must also hold this site's origins.
const getGoogleSignInId = () => {
  switch (ENV) {
    case 'qa':
      return '289196470141-9o3uroubbjmlpmjhffaorvfmhtefb9h5.apps.googleusercontent.com'
    case 'production':
      return '661316390459-1d6d8h4rqk3d79sh5nhoqsc3q7ud4slk.apps.googleusercontent.com'
    default:
      return '79655950420-7advifufk6pkvemumpf2c7r70evnjfar.apps.googleusercontent.com'
  }
}

const getRecaptchaV3SiteKey = () => {
  switch (ENV) {
    case 'production':
      return '6LcuHfctAAAAAJmYRYygsfNXOuzgpfKF-VY7pzEN'
    default:
      return '6LdfnvYtAAAAABc1AS0lqzB_H98bUhv4arTrICNj'
  }
}

// One container per environment. Production gets its own ID or none, never
// the pre-production container's.
const getGtmId = () => {
  switch (ENV) {
    case 'production':
      return 'GTM-WJ6L9PJP'
    default:
      return 'GTM-5LDSNCMH'
  }
}

const getNeatDataUrl = () => {
  switch (ENV) {
    case 'qa':
      return 'https://www-qa-data.neat.com'
    case 'production':
      return 'https://www-data.neat.com'
    default:
      return 'https://www-staging-data.neat.com'
  }
}

export const GOOGLE_SIGN_IN_ID = getGoogleSignInId()
export const RECAPTCHA_V3_SITE_KEY = getRecaptchaV3SiteKey()
export const NEAT_DATA_URL = getNeatDataUrl()

export const COMPANY_NAV = [
  { name: 'ourHistory' },
  { name: 'support', external: true, openTab: true, to: SUPPORT_URL },
  { name: 'policies' },
]

// One key per line: a top-level key replaces the shared default whole, and a
// required key left unset fails at mount.
export const SITE_CONFIG = {
  sources: {
    trial: 'tryneat.com Trial',
    checkout: 'tryneat.com Checkout',
    backupTrial: 'tryneat.com order error no-cc trial',
    abandonedCart: 'tryneat.com Abandoned Cart',
    trialCta: 'tryneat.com CC Trial',
    newsletter: 'tryneat.com newsletter',
  },
  links: {
    terms: '/terms-conditions',
    privacy: '/policies/privacy',
    home: '/',
  },
  tracking: {
    gtmId: getGtmId(),
    buyFlowGtmId: null,
    uetTagId: null,
    facebookPixelId: null,
    adsConversionSendTo: null,
    capterraTrackerSrc: null,
    successEvents: false,
    dataLayerPurchase: true,
  },
  defaultSku: 'neatdocsyr',
  trialSku: 'neatdocsyrt',
  planSelect: false,
  upsell: false,
  signUpHeading: {
    skuVariants: {
      neatdocsyrt: 'trialNeatDocs',
      neatdocsyr: 'buyNeatDocs',
    },
    variants: {
      trialNeatDocs: { headingId: 'trialNeatDocs', subheadingId: 'trialNeatDocsSubhead' },
      buyNeatDocs: { headingId: 'buyNeatDocs', subheadingId: null },
    },
  },
  panels: {
    accountSetup: CheckoutSideEmailNeatDocs,
    billingTrial: CheckoutSideCreditCardTrialNeatDocs,
    billingBuy: CheckoutSideCreditCardNeatDocs,
  },
  messages: {
    'pages.signUp.form.authenticate.heading.trialNeatDocs': 'Start your <em>FREE</em> 14-day,<br></br>risk-free NeatDocs trial',
    'pages.signUp.form.authenticate.heading.trialNeatDocsSubhead': 'Every receipt, bill and document in one place.',
    'pages.signUp.form.authenticate.heading.buyNeatDocs': 'Get NeatDocs today.<br></br><em>30-day money back</em><br></br>guarantee!',
    copyright: `Copyright ${new Date().getFullYear()} Neat | All Rights Reserved`,
    'cta.tryNeatFree': 'Try Neat Free',
    'cta.tryNeatFREE': 'Try Neat FREE',
    'cta.getStarted': 'Get Started',
    'cta.signUpNow': 'Sign up now',
    'cta.unsubscribe': 'Unsubscribe',
    'featuresFooter.header': "Let's Get Started",
    'featuresFooter.p': 'Ready for more confidence?{br}Try Neat today for free.',
    'pages.notFound.h2': 'Oh No! The page you requested could not be found.',
    'pages.notFound.popular': 'Popular Pages:',
    'pages.notFound.links.support': 'Neat’s Support Center',
    'pages.notFound.links.home': 'Home',
    'pages.notFound.links.startFreeTrial': 'Start Free Trial',
    'pages.notFound.links.contactUs': 'contact us',
    'pages.notFound.links.cannotFind': 'If you can’t find what you’re looking for, {link}.',
    'pages.policies.email': 'Email Preferences',
    'pages.policies.privacy': 'Privacy Policy',
    'pages.policies.salesTax': 'Sales Tax Policy',
    'pages.policies.terms': 'Terms & Conditions',
    'pages.emailPreferences.subscribe.header': 'Subscribe',
    'pages.emailPreferences.subscribe.p1': 'Subscribe here to get tips and offers designed to help grow your business straight to your inbox.',
    'pages.emailPreferences.subscribe.p2': 'We send emails a few times a month, so once you subscribe you can expect to hear from us soon.',
    'pages.emailPreferences.unsubscribe.header': 'Unsubscribe',
    'pages.emailPreferences.unsubscribe.p1': 'If you would like to unsubscribe from receiving marketing emails from Neat, you may opt out here. By opting out, you will no longer receive tips and offers from Neat. You will still receive important account and billing related emails from Neat.',
    'pages.emailPreferences.unsubscribe.success': 'Your opt-out request has been confirmed. Please allow 48 hours for your request to fully process. You can re-subscribe anytime by filling out the form above.',
    'footer.navLists.company': 'Company',
    'footer.mailingList.header': 'Subscribe',
    'footer.mailingList.label': 'Subscribe to get tips and offers designed to help grow your business.',
    'navLinks.ourHistory': 'About Neat',
    'navLinks.support': 'Support',
    'navLinks.policies': 'Policy Center',
    'pages.home.faq.title': 'Questions about NeatDocs',
    'pages.home.faq.subtitle': 'Everything you need to know before getting started.',
    'pages.home.faq.whatItDoes.title': 'What exactly does NeatDocs do?',
    'pages.home.faq.whatItDoes.p': 'NeatDocs is a document capture and organization system. It gives you a secure, searchable place to store every receipt, bill, invoice, and business document. When a new document arrives — from your phone, email, or desktop — NeatDocs automatically classifies it by type and adds it to your file cabinet so nothing gets lost. Think of it as replacing the shoebox, the email inbox, and the paper pile with one organized system.',
    'pages.home.faq.taxTime.title': 'Will NeatDocs help me at tax time?',
    'pages.home.faq.taxTime.p': 'Yes — that\'s one of its primary jobs. When everything is captured and organized in NeatDocs throughout the year, tax season becomes stress-free. With everything organized in one place, you\'re no longer scrambling to find receipts. Everything is labeled and searchable, so nothing gets missed.',
    'pages.home.faq.rentalHosts.title': 'I\'m an Airbnb or VRBO host. Is NeatDocs right for me?',
    'pages.home.faq.rentalHosts.p': 'NeatDocs is a great fit for short-term rental hosts. Every repair receipt, cleaning invoice, utility bill, and maintenance document can be captured the moment it arrives — via your phone, email forward, or web upload. Organize them by property or month, and at year-end your accountant has everything they need to maximize your deductions. No spreadsheets, no paper piles, no panic.',
    'pages.home.faq.gettingDocsIn.title': 'How do I get my documents into NeatDocs?',
    'pages.home.faq.gettingDocsIn.p': 'Three ways: snap a photo with the NeatDocs mobile app (iOS and Android), forward any emailed receipt or bill to your personal NeatDocs email address, or drag and drop files from your desktop via the web app. All three methods work automatically — documents are classified and filed the moment they arrive.',
    'pages.home.faq.security.title': 'Is my data secure in NeatDocs?',
    'pages.home.faq.security.p': 'Yes. NeatDocs uses bank-level encryption to protect all your documents and data. Your files are stored securely in the cloud, accessible only to you, and backed up automatically. Neat has been trusted by 2 million+ small businesses for over 20 years.',
  },
}
